/* Main app — Vite + React build. */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Icon } from './icons.jsx';
import {
  useTweaks, TweaksPanel, TweakSection, TweakSlider, TweakSelect,
} from './tweaks-panel.jsx';
import { realFill } from './real-fill.js';
import {
  Cell, ConfMeter, StatusBadge, Spreadsheet,
  exportXlsx, exportToOriginalWorkbook,
} from './spreadsheet.jsx';
import { parseRfpWorkbook } from './xlsx-parser.js';
import { PRODUCTS, PRODUCT } from './products.js';
import { MODELS, DEFAULT_MODEL } from './models.js';
import { WMS_RKB } from './rkb-data.js';
import { DEFAULT_VOCAB, roleOf, labelsOf } from './status-vocab.js';
import { prescanWorkbook } from './prescan.js';
import logoUrl from './assets/wm-synergy-logo-dark.png';

export default function App() {
  const [tweaks, setTweak] = useTweaks({
    batchSize: 12,
    concurrency: 96,
    model: DEFAULT_MODEL,
  });

  const [rfpFile, setRfpFile] = useState(null);
  const [parsed, setParsed] = useState(null); // { workbook, questions, detection, skippedSheets }
  // Answer vocabulary for the run. Replaced by the pre-scan when the RFP
  // mandates its own response categories.
  const [vocab, setVocab] = useState(DEFAULT_VOCAB);
  const [instructions, setInstructions] = useState(null);
  const [product, setProduct] = useState(null);
  const [phase, setPhase] = useState('idle'); // idle | running | done | error
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [logs, setLogs] = useState([]);
  const [rows, setRows] = useState([]);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('sheet');
  const [elapsed, setElapsed] = useState(0);
  const [toasts, setToasts] = useState([]);

  const startTimeRef = useRef(null);
  const timerRef = useRef(null);
  const cancelRef = useRef(false);
  const logRef = useRef(null);

  // Tick timer
  useEffect(() => {
    if (phase !== 'running') return;
    timerRef.current = setInterval(() => {
      setElapsed(Date.now() - startTimeRef.current);
    }, 500);
    return () => clearInterval(timerRef.current);
  }, [phase]);

  // Alt+T opens the Tweaks panel (standalone build — no host toolbar).
  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey && e.key.toLowerCase() === 't') {
        e.preventDefault();
        window.postMessage({ type: '__toggle_edit_mode' }, '*');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const toast = (msg, type = 'info') => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, msg, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3400);
  };

  const log = (msg, type = '') => {
    setLogs(prev => [...prev, {
      t: new Date().toLocaleTimeString([], { hour12: false }),
      msg, type
    }]);
  };

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logs]);

  const onFile = async (f) => {
    if (!f) return;
    setRfpFile(f);
    setParsed(null);
    log(`RFP uploaded: ${f.name} (${(f.size / 1024).toFixed(0)} KB)`, 'info');
    try {
      const res = await parseRfpWorkbook(f);
      setParsed(res);
      setVocab(DEFAULT_VOCAB);
      setInstructions(null);
      const sheetCount = Object.keys(res.detection).length;
      log(`Parsed ${res.questions.length} questions across ${sheetCount} sheet${sheetCount === 1 ? '' : 's'}`, 'ok');
      for (const [sheet, det] of Object.entries(res.detection)) {
        if (det.appendedAnswerCol) {
          log(`${sheet}: response column already contains client data — answers will go to a new appended column`, 'warn');
        }
        if (det.truncated) {
          log(`${sheet}: sheet is longer than the row ceiling — later rows will not be answered`, 'warn');
        }
      }
      for (const sk of res.skippedSheets || []) {
        log(`${sk.sheet}: detected but not answered — ${sk.reason}`, 'warn');
      }
    } catch (e) {
      log(`Parse failed: ${e.message}`, 'err');
    }
  };

  const stats = useMemo(() => {
    const s = { met: 0, partial: 0, gap: 0, total: rows.length };
    rows.forEach(r => { s[roleOf(vocab, r.status)]++; });
    return s;
  }, [rows, vocab]);

  // Review queue is driven by confidence only — not by status. A high-confidence
  // Gap is a definitive "does not meet"; a low-confidence Met still needs eyes.
  const reviewRows = useMemo(
    () => rows.filter(r => (r.confidence || 0) < 0.6),
    [rows]
  );

  function cancelRun() {
    cancelRef.current = true;
    log('Canceled by user', 'warn');
  }

  async function runFill() {
    if (!rfpFile || !product) return;
    cancelRef.current = false;
    setPhase('running');
    setLogs([]);
    setRows([]);
    setProgress(0);
    setElapsed(0);
    startTimeRef.current = Date.now();

    const productLabel = PRODUCT[product]?.chip || product;
    log('Session started — clock is running', 'info');
    log('RFP: ' + rfpFile.name, 'info');
    log('Product: ' + productLabel + ' · model ' + tweaks.model, 'info');

    try {
      if (cancelRef.current) throw new Error('canceled');

      if (!parsed?.questions?.length) {
        throw new Error('No questions detected in the uploaded workbook — check the file and try again.');
      }
      const questions = parsed.questions;

      // Pre-scan: read the RFP's own response instructions before answering, so
      // a workbook that mandates its own categories is answered in them.
      setProgressLabel('Reading RFP instructions…');
      setProgress(2);
      let runVocab = DEFAULT_VOCAB;
      let runInstructions = null;
      if (parsed.workbook) {
        const scan = await prescanWorkbook({ workbook: parsed.workbook, model: tweaks.model, onLog: log });
        runVocab = scan.vocab || DEFAULT_VOCAB;
        runInstructions = scan.instructions;
        setVocab(runVocab);
        setInstructions(runInstructions);
      }
      if (cancelRef.current) throw new Error('canceled');

      setProgressLabel('Drafting responses…');
      setProgress(5);

      const run = await realFill({
        product,
        questions,
        vocab: runVocab,
        instructions: runInstructions,
        model: tweaks.model,
        batchSize: tweaks.batchSize,
        concurrency: tweaks.concurrency,
        onLog: log,
        onBatch: ({ done, total }) => {
          const batchFrac = done / total;
          setProgress(5 + batchFrac * 90);
          setProgressLabel(`Batch ${done} of ${total}\u2026`);
        },
        cancelRef,
      });

      setRows(run.rows);
      setProgress(100);
      setProgressLabel('Complete');
      const totalElapsed = Math.round((Date.now() - startTimeRef.current) / 1000);
      log(`Done in ${totalElapsed}s — ${Object.entries(run.tally).map(([l, c]) => `${l} ${c}`).join(' · ')}`, 'ok');
      setPhase('done');
      setElapsed(Date.now() - startTimeRef.current);

      setHistory(h => [{
        id: Date.now(),
        file: rfpFile.name,
        product,
        secs: totalElapsed,
        met: run.met,
        total: run.total,
        ts: new Date(),
      }, ...h].slice(0, 8));

      toast('RFP response ready — ' + run.total + ' questions answered', 'success');
    } catch (e) {
      if (e.message === 'canceled') {
        setPhase('idle');
        toast('Run canceled', 'info');
      } else {
        log('Error: ' + e.message, 'err');
        setPhase('error');
        toast('Run failed: ' + e.message, 'error');
      }
    }
  }

  const download = () => {
    const base = (rfpFile?.name || 'RFP').replace(/\.xlsx?$/i, '');
    const productLabel = PRODUCT[product]?.chip || product;

    // If we parsed the original workbook AND answers have row/col metadata,
    // write back into the original file to preserve customer formatting.
    const canWriteBack =
      parsed?.workbook &&
      parsed?.detection &&
      rows.every((r) => typeof r.row === 'number' && typeof r.col === 'number');

    const exportMeta = {
      vocab,
      product: productLabel,
      source: rfpFile?.name,
      model: tweaks.model,
      rkbVersion: `${WMS_RKB.rkb_meta.version} (${WMS_RKB.rkb_meta.last_updated})`,
      skippedSheets: parsed?.skippedSheets || [],
      instructions,
    };

    if (canWriteBack) {
      const fname = `${base} — WM Synergy Response.xlsx`;
      exportToOriginalWorkbook(parsed.workbook, rows, parsed.detection, fname, exportMeta);
      toast('Downloaded ' + fname, 'success');
    } else {
      const fname = `${base} — WM Synergy Response.xlsx`;
      exportXlsx(rows, fname, exportMeta);
      toast('Downloaded ' + fname + ' (summary format)', 'success');
    }
  };

  const elapsedStr = useMemo(() => {
    const ms = elapsed || 0;
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0');
  }, [elapsed]);

  const productLabel = PRODUCT[product]?.chip || null;

  const rkbAge = useMemo(() => {
    const updated = WMS_RKB.rkb_meta?.last_updated;
    if (!updated) return null;
    const days = Math.floor((Date.now() - new Date(updated).getTime()) / 86400000);
    return Number.isFinite(days) ? { days, updated, stale: days > 90 } : null;
  }, []);

  return (
    <>
      <header className="app-header">
        <img src={logoUrl} alt="WM Synergy" className="brand-logo"/>
        <span className="brand-sub">RFP Filler</span>
        <div className="header-right">
          {productLabel ? (
            <span className="product-chip"><Icon name="spark" size={12}/> {productLabel}</span>
          ) : (
            <span className="product-chip empty">No product selected</span>
          )}
          <span
            className={'version-chip' + (rkbAge?.stale ? ' stale' : '')}
            title={rkbAge ? `RKB last updated ${rkbAge.updated} (${rkbAge.days} days ago)` : ''}
          >
            v20.8 · RKB {WMS_RKB.rkb_meta.version}
            {rkbAge ? ` · ${rkbAge.updated}` : ''}
          </span>
        </div>
      </header>

      <div className="app-shell">
        <aside className="rail">
          <div>
            <div className="eyebrow">Upload RFP</div>
            <label className={'drop' + (rfpFile ? ' loaded' : '')}>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => onFile(e.target.files?.[0])}
              />
              <div className="drop-inner">
                <div className="drop-icon">
                  <Icon name={rfpFile ? 'check' : 'upload'} size={18}/>
                </div>
                <div className="drop-text">
                  <div className="drop-title">{rfpFile?.name || 'Drop RFP here'}</div>
                  <div className="drop-sub">
                    {rfpFile
                      ? (parsed
                          ? `${parsed.questions.length} questions detected`
                          : `${(rfpFile.size / 1024).toFixed(0)} KB`)
                      : 'click to browse · .xlsx .xls'}
                  </div>
                </div>
              </div>
            </label>

            {/* What the parser found — and what it deliberately left alone.
                Before this, skipped sheets and appended columns were dropped
                silently and the user only saw a bare question count. */}
            {parsed && (
              <div className="parse-report">
                {Object.entries(parsed.detection).map(([sheet, det]) => (
                  <div key={sheet} className="parse-row">
                    <span className="parse-sheet">{sheet}</span>
                    <span className="parse-note">
                      {parsed.questions.filter((q) => q.sheet === sheet).length} questions
                      {det.appendedAnswerCol && ' · answers appended to a new column'}
                      {det.truncated && ' · sheet truncated'}
                    </span>
                  </div>
                ))}
                {(parsed.skippedSheets || []).map((sk) => (
                  <div key={sk.sheet} className="parse-row skipped">
                    <span className="parse-sheet">{sk.sheet}</span>
                    <span className="parse-note">not answered — {sk.reason}</span>
                  </div>
                ))}
                {vocab.source !== 'default' && (
                  <div className="parse-row">
                    <span className="parse-sheet">Categories</span>
                    <span className="parse-note">{labelsOf(vocab).join(' · ')}</span>
                  </div>
                )}
              </div>
            )}

            {rkbAge?.stale && (
              <div className="rkb-warning">
                Knowledge base is {rkbAge.days} days old (v{WMS_RKB.rkb_meta.version}, {rkbAge.updated}).
                Answers may miss recent product changes — refresh <code>src/rkb-data.js</code> before relying on this run.
              </div>
            )}
          </div>

          <div>
            <div className="eyebrow">Select Product</div>
            <div className="product-grid">
              {PRODUCTS.map((p) => (
                <button
                  key={p.key}
                  className={'product-card' + (product === p.key ? ' selected' : '')}
                  onClick={() => setProduct(p.key)}
                >
                  <div className="p-name">{p.name}</div>
                  <div className="p-sub">{p.sub}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <button
              className={'btn-primary' + (phase === 'running' ? ' running' : '')}
              disabled={!rfpFile || !product || phase === 'running'}
              onClick={runFill}
            >
              {phase === 'running' ? (
                <><Icon name="spin" size={15} style={{ animation: 'spin .8s linear infinite' }}/> Running · {Math.round(progress)}%</>
              ) : (
                <><Icon name="play" size={13}/> Fill RFP</>
              )}
            </button>
            {phase === 'running' && (
              <button className="btn-cancel" onClick={cancelRun}>Cancel run</button>
            )}
          </div>

          <div>
            <div className="eyebrow">Run history</div>
            <div className="run-history">
              {history.length === 0 ? (
                <div className="empty-hint">No runs yet this session</div>
              ) : history.map(h => (
                <div key={h.id} className="run-entry">
                  <span className="run-file">{h.file}</span>
                  <span className="run-meta">{h.met}/{h.total} · {h.secs}s</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-auto"></div>
        </aside>

        <main className="canvas">
          {phase === 'idle' && !rows.length && (
            <div className="idle">
              <div className="idle-mark">
                <Icon name="table" size={26} color="var(--red)"/>
              </div>
              <h2>Ready to fill</h2>
              <p>Drop an RFP workbook, pick a product, and WM Synergy's RKB auto-answers every question. Review, edit inline, and export a completed spreadsheet.</p>
              <div className="step-list">
                <div className={'step ' + (rfpFile ? 'done' : 'active')}>
                  <div className="step-dot">{rfpFile ? <Icon name="check" size={13}/> : '1'}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="step-label">{rfpFile ? 'RFP uploaded' : 'Upload an RFP workbook'}</div>
                    <div className="step-hint">{rfpFile?.name || '.xlsx / .xls'}</div>
                  </div>
                </div>
                <div className={'step ' + (product ? 'done' : rfpFile ? 'active' : '')}>
                  <div className="step-dot">{product ? <Icon name="check" size={13}/> : '2'}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="step-label">{product ? (productLabel + ' selected') : 'Select a product'}</div>
                    <div className="step-hint">{PRODUCTS.map((p) => p.name).join(' · ')}</div>
                  </div>
                </div>
                <div className={'step ' + (rfpFile && product ? 'active' : '')}>
                  <div className="step-dot">3</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="step-label">Hit Fill RFP</div>
                    <div className="step-hint">Typical run: 30–90 seconds</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {phase !== 'idle' && (
            <div className="clock-block">
              <div className="clock-display">{elapsedStr}</div>
              <div className="clock-info">
                <div className="clock-status">
                  <span className={'clock-pulse ' + (phase === 'running' ? '' : phase === 'done' ? 'done' : 'idle')}></span>
                  {phase === 'running' ? (progressLabel || 'Working\u2026') :
                   phase === 'done' ? 'Complete' :
                   phase === 'error' ? 'Error' : 'Initializing\u2026'}
                </div>
                <div className="clock-easter">Relax, you could be doing this manually.</div>
              </div>
            </div>
          )}

          {phase === 'running' && (
            <div className="progress-block">
              <div className="progress-head">
                <span className="progress-label">{progressLabel}</span>
                <span className="progress-pct">{Math.round(progress)}%</span>
              </div>
              <div className="progress-track">
                <div className="progress-fill" style={{ width: progress + '%' }}/>
              </div>
              <div className="log-stream" ref={logRef}>
                {logs.map((l, i) => (
                  <div key={i} className={'log-line ' + (l.type || '')}>
                    <span className="t">{l.t}</span>{l.msg}
                  </div>
                ))}
              </div>
            </div>
          )}

          {rows.length > 0 && (
            <>
              <div className="stats-grid">
                <div className="stat-card met">
                  <div className="stat-accent"></div>
                  <div className="stat-num">{stats.met}</div>
                  <div className="stat-label">Met</div>
                  <div className="stat-sub">{stats.total ? Math.round(stats.met / stats.total * 100) : 0}% of total</div>
                </div>
                <div className="stat-card partial">
                  <div className="stat-accent"></div>
                  <div className="stat-num">{stats.partial}</div>
                  <div className="stat-label">Partial</div>
                  <div className="stat-sub">partially meets req</div>
                </div>
                <div className="stat-card gap">
                  <div className="stat-accent"></div>
                  <div className="stat-num">{stats.gap}</div>
                  <div className="stat-label">Gaps</div>
                  <div className="stat-sub">does not meet req</div>
                </div>
                <div className="stat-card total">
                  <div className="stat-accent"></div>
                  <div className="stat-num">{stats.total}</div>
                  <div className="stat-label">Total</div>
                  <div className="stat-sub">{Math.round(elapsed / 1000)}s runtime</div>
                </div>
              </div>

              <div className="output-block">
                <div className="output-head">
                  <div className="output-title-wrap">
                    <div className="output-title">
                      {(rfpFile?.name || 'RFP').replace(/\.xlsx?$/i, '')} — Response
                    </div>
                    <div className="output-meta">
                      {productLabel} · {new Date().toLocaleDateString()} · {Math.round(elapsed / 1000)}s
                    </div>
                  </div>
                  <div className="output-actions">
                    <button className="btn-ghost" onClick={() => window.print()}>
                      <Icon name="print" size={14}/> Print
                    </button>
                    <button className="btn-download" onClick={download}>
                      <Icon name="download" size={14}/> Download .xlsx
                    </button>
                  </div>
                </div>

                <div className="tab-bar">
                  <div className={'tab' + (activeTab === 'sheet' ? ' active' : '')} onClick={() => setActiveTab('sheet')}>
                    <Icon name="sheet" size={13}/> Workbook <span className="count">{rows.length}</span>
                  </div>
                  <div className={'tab' + (activeTab === 'review' ? ' active' : '')} onClick={() => setActiveTab('review')}>
                    <Icon name="warn" size={13}/> Review queue <span className="count">{reviewRows.length}</span>
                  </div>
                </div>

                <div className="tab-body">
                  {activeTab === 'sheet' && <Spreadsheet rows={rows} setRows={setRows} vocab={vocab}/>}
                  {activeTab === 'review' && <ReviewTable rows={reviewRows} setRows={setRows} jumpToWorkbook={() => setActiveTab('sheet')} toast={toast}/>}
                </div>
              </div>
            </>
          )}
        </main>
      </div>

      <div className="toast-wrap">
        {toasts.map(t => (
          <div key={t.id} className={'toast ' + t.type}>
            <span className="toast-icon" style={{
              color: t.type === 'success' ? '#22c55e' : t.type === 'error' ? '#f87171' : '#60a5fa'
            }}>
              {t.type === 'success' ? '✓' : t.type === 'error' ? '✕' : 'ℹ'}
            </span>
            <span className="toast-msg">{t.msg}</span>
          </div>
        ))}
      </div>

      <AppTweaks tweaks={tweaks} setTweak={setTweak}/>
    </>
  );
}

function ReviewTable({ rows, setRows, jumpToWorkbook, toast }) {
  const updateRow = (id, patch) =>
    setRows(prev => prev.map(r => r.id === id ? { ...r, ...patch, edited: true } : r));

  const markVerified = (r) => {
    updateRow(r.id, { confidence: 1 });
    toast && toast(`Verified · removed from review queue`, 'success');
  };

  if (!rows.length) {
    return (
      <div className="review-empty">
        <h3>All clear</h3>
        <div style={{ fontSize: 14 }}>Every answer landed at ≥ 60% confidence — nothing to review. Edit any row in the Workbook tab.</div>
      </div>
    );
  }
  const editedCount = rows.filter(r => r.edited).length;
  return (
    <>
      <div className="review-intro">
        <strong>Review queue</strong> — answers with model confidence &lt; 60%.
        Status (Met / Partial / Gap) is separate: it describes how the <em>product</em> fits the requirement.
        Edits to response or status save into the run and appear in the downloaded .xlsx.
      </div>
      <div className="review-list">
        {rows.map(r => (
          <div key={r.id} className={'review-card status-' + r.status.toLowerCase()}>
            <div className="review-card-head">
              <div className="rc-section">{r.sheet}</div>
              <div className="rc-meta">
                <StatusBadge status={r.status} vocab={vocab}/>
                <ConfMeter value={r.confidence}/>
                {r.edited && <span className="edit-flag" title="Edited since run"></span>}
              </div>
            </div>
            <div className="rc-question">{r.question}</div>
            <div className="rc-why">
              {r.status_rationale && (
                <div className="rc-why-row">
                  <span className="rc-why-tag">Status</span>
                  <span>{r.status_rationale}</span>
                </div>
              )}
              {r.confidence_rationale && (
                <div className="rc-why-row">
                  <span className="rc-why-tag">Confidence</span>
                  <span>{r.confidence_rationale}</span>
                </div>
              )}
              {r.citations && r.citations.length > 0 && (
                <div className="rc-why-row">
                  <span className="rc-why-tag">Citations</span>
                  <span className="rc-cites">
                    {r.citations.map((c, idx) => <code key={idx} className="cite-chip">{c}</code>)}
                  </span>
                </div>
              )}
            </div>
            <div className="rc-answer-wrap">
              <label className="rc-answer-label">Response <span className="rc-answer-hint">(editable)</span></label>
              <Cell value={r.answer} onChange={(v) => updateRow(r.id, { answer: v })}/>
            </div>
            <div className="rc-actions">
              <select
                value={r.status}
                onChange={(e) => updateRow(r.id, { status: e.target.value })}
                className={'status-select status-' + roleOf(vocab, r.status)}
              >
                {labelsOf(vocab).map((l) => <option key={l}>{l}</option>)}
              </select>
              <div className="spacer"/>
              <button
                className="btn-ghost sm"
                onClick={() => markVerified(r)}
                title="Mark as verified — removes from review queue"
              >
                <Icon name="check" size={12}/> Mark verified
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="review-save-bar">
        <Icon name="check" size={13} color="var(--success)"/>
        <span>Edits flow into the downloaded .xlsx — no save button needed.</span>
        {editedCount > 0 && (
          <span style={{ color: 'var(--red)', fontFamily: 'var(--font-mono)', fontSize: 11.5 }}>
            {editedCount} edited
          </span>
        )}
        <div className="spacer"/>
        <button className="btn-review-jump" onClick={jumpToWorkbook}>
          <Icon name="sheet" size={12}/> Open in Workbook
        </button>
      </div>
    </>
  );
}

function AppTweaks({ tweaks, setTweak }) {
  return (
    <TweaksPanel title="Tweaks">
      <TweakSection label="Model"/>
      <TweakSelect
        label="Claude model"
        value={tweaks.model}
        options={MODELS}
        onChange={(v) => setTweak('model', v)}
      />
      <TweakSection label="Parallel batching"/>
      <TweakSlider
        label="Questions per batch"
        value={tweaks.batchSize} min={2} max={30} step={1}
        onChange={(v) => setTweak('batchSize', v)}
      />
      <TweakSlider
        label="Concurrent batches"
        value={tweaks.concurrency} min={1} max={96} step={1}
        onChange={(v) => setTweak('concurrency', v)}
      />
    </TweaksPanel>
  );
}
